import { useTranslation } from 'react-i18next';
import { CloudOff, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/cn';
import { Loader } from '@/components/common/Loader';

/**
 * Il riquadro che una sezione mostra quando NON ha un elenco da mostrare e il vuoto
 * sarebbe una bugia.
 *
 * 🔴 Nasce dal guasto misurato il 2026-09-06 su `stg-tma` con tutti i tab della Hub
 * accesi: tre pannelli su otto affermavano un vuoto — «No tournaments in this
 * category», «Nothing to watch right now» — su domande **mai poste**, perche' la
 * fonte e' spenta da un flag di build su questo tenant. Un vuoto affermato e' peggio
 * sia dello spinner (che tace) sia dell'errore (che dice di non sapere).
 *
 * Due toni, e la differenza NON e' cosmetica — e' cio' che l'utente puo' farci:
 *  · `unavailable`: la chiamata **non parte**. Riprovare non serve, e il testo non lo
 *    promette. E' uno stato della BUILD, non della rete.
 *  · `error`: la chiamata e' partita e non ha dato una risposta usabile. Riprovare
 *    puo' servire.
 *
 * ⛔ Non unificarli in un solo messaggio «qualcosa e' andato storto»: rifonderebbe
 * esattamente la distinzione che questo file esiste per tenere aperta.
 *
 * Il testo vive in `general.json` (`panelNotice.*`) ed e' RIUSATO, non copiato: due
 * frasi identiche sotto due chiavi divergono alla prima revisione, e poi la stessa app
 * dice due cose diverse dello stesso fatto.
 */
export function PanelNotice({
  tone,
  detail,
  via,
  onRetry,
  retrying = false,
  className,
}: {
  tone: 'unavailable' | 'error';
  /** Riga aggiuntiva sotto il testo standard: cosa, di preciso, non ha risposto. */
  detail?: string;
  /**
   * **La strada alternativa**, una frase: dove l'utente puo' andare INVECE.
   *
   * ⚠️ Separato da `detail` e non fuso con esso, benche' entrambi siano «una riga in piu'»:
   * `detail` e' una DIAGNOSI (a chi legge non serve per agire, e infatti e' minuscolo),
   * `via` e' un'ISTRUZIONE. Fonderli darebbe un solo campo per due fatti opposti — la
   * stessa forma di guasto che questo file esiste per non commettere — e la diagnosi
   * finirebbe scritta come se fosse una strada.
   *
   * 📌 **Se una sezione non ha una strada alternativa VERA, questo resta vuoto**: meglio
   * nessuna strada che una che non porta da nessuna parte, e non se ne inventa una per
   * riempire lo slot. (Decisione di `or0`, 10/09.)
   */
  via?: string;
  /**
   * «Riprova»: rilancia la lettura. ⛔ Solo col tono `error` — con `unavailable` la chiamata non
   * parte, e un bottone che promette un nuovo tentativo mentirebbe (vedi sopra): li' non si disegna
   * nemmeno se il chiamante lo passa.
   */
  onRetry?: () => void;
  /**
   * La lettura e' gia' in volo: «Riprova» resta a schermo ma SPENTO. ⚠️ Non e' cosmetico — con una
   * richiesta `pending` il `refetch` di RTK non fa niente, e un bottone vivo che non fa niente e' una
   * promessa falsa (il caso vero: la frase d'errore dopo 45 s senza risposta, `NO_ANSWER_MS`).
   */
  retrying?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('general');
  const Icon = tone === 'unavailable' ? CloudOff : TriangleAlert;
  return (
    <div
      role="status"
      data-testid="stato-avviso"
      className={cn(
        'flex h-full flex-col items-center justify-center gap-3 px-6 py-8 text-center',
        className,
      )}
    >
      <Icon
        aria-hidden
        className={cn('h-8 w-8', tone === 'unavailable' ? 'text-fg-muted' : 'text-danger')}
      />
      <p className="text-base font-bold text-fg">
        {tone === 'unavailable'
          ? // ⛔ Niente «backend», niente «build»: sono parole NOSTRE, e un utente che le legge
            // impara un dettaglio d'infrastruttura invece di una notizia. ⛔ E nessuna data,
            // nessuna promessa di ritorno (decisione di Luca del 31/08, la stessa che regge la
            // copy di `routes/limulo/LimuloDegraded.tsx`).
            t('panelNotice.unavailable.title', { defaultValue: 'This section is changing engine' })
          : t('panelNotice.error.title', { defaultValue: "Couldn't load this section" })}
      </p>
      <p className="max-w-[20rem] text-sm text-fg-muted">
        {tone === 'unavailable'
          ? // La seconda meta' della copy precedente sopravvive parola per parola: e' la parte
            // migliore che c'era, e non c'era ragione di riscriverla. Sparisce solo la prima
            // meta', che nominava l'infrastruttura.
            t('panelNotice.unavailable.body', {
              defaultValue: 'It is not an empty list: it is a list that was not requested.',
            })
          : t('panelNotice.error.body', {
              defaultValue: 'The service did not answer. Nothing here is a statement about your account.',
            })}
      </p>
      {/* La strada alternativa si legge come il corpo, non come una nota a pie' di pagina:
          e' l'unica riga su cui l'utente puo' agire. */}
      {via && <p className="max-w-[20rem] text-sm font-medium text-fg">{via}</p>}
      {detail && <p className="max-w-[20rem] text-[11px] text-fg-muted opacity-80">{detail}</p>}
      {onRetry && tone === 'error' && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          aria-busy={retrying || undefined}
          className="rounded-xl bg-bg-tertiary px-6 py-2.5 text-sm font-bold text-fg transition-opacity active:opacity-80 disabled:opacity-50"
        >
          {t('panelNotice.error.retry', { defaultValue: 'Retry' })}
        </button>
      )}
    </div>
  );
}

/**
 * L'attesa che dura troppo: lo spinner RESTA — stiamo ancora aspettando davvero — e sotto
 * compare una riga che lo dice.
 *
 * 🔴 Non e' un errore, ed e' esattamente il punto. Misurato su `stg-tma` il 2026-09-06
 * (or45, campionando `animate-spin` a 5/15/25/35 s): le schede muta per 15-25 secondi si
 * sbloccano DA SOLE e dicono l'errore vero. Dichiarare `error` a 12 s avrebbe affermato un
 * fallimento non ancora avvenuto — la stessa colpa del vuoto affermato, commessa dalla
 * difesa contro di esso.
 *
 * ⛔ Questa riga CEDE il posto: appena arriva l'errore vero o arrivano i dati, il verdetto
 * cambia e questo componente sparisce. Non promette che finira' bene, e non dice che e'
 * finita male.
 */
export function PanelWaiting({
  slow,
  /** `false` quando lo spinner lo disegna gia' il chiamante (il cerchio di Ranked). */
  spinner = true,
  className,
}: {
  slow: boolean;
  spinner?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('general');
  return (
    <div data-testid="stato-attesa" className={cn('flex h-full flex-col items-center justify-center gap-3', className)}>
      {spinner && <Loader />}
      {slow && (
        <p className="max-w-[18rem] px-6 text-center text-xs text-fg-muted">
          {t('panelNotice.slow', {
            defaultValue: 'This is taking longer than usual — still waiting for an answer.',
          })}
        </p>
      )}
    </div>
  );
}
